import { Breakpoints } from '@angular/cdk/layout';
import { ChangeDetectionStrategy, Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import {firstValueFrom, from} from 'rxjs';

import { UICollectionService } from '@sim/collections/services';
import { RoleService } from '@sim/role/services/role.service';
import {
  BaseTableFilteredComponent,
  basicDeleteTemplateOptions,
  basicEditTemplateOptions,
  basicShowTemplateOptions,
  TableContextMenu,
  TableField,
  TableFilterType,
  TableSortType,
  UiTableComponent,
} from '@ui/ui.module';

import { BaseApiService, SwalService } from '@atmi/core';
import { Base, BaseFilter, BaseFilterMeta } from '@atmi/core/models';
import { BaseFiltersComponent } from '@atmi/shared';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-base',
  styleUrls: ['./base.component.scss'],
  templateUrl: './base.component.html',
})
export class BaseComponent
  extends BaseTableFilteredComponent<Base, BaseFilter>
  implements UiTableComponent<Base> {

  readonly MobileFiltersComponent = BaseFiltersComponent;

  readonly fields: TableField<Base>[] = [
    {
      header: '',
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'chekbox',
      selectable: true,
    },
    {
      header: this.translate.instant('Codice unico'),
      key: 'codiceUnico',
      sortable: true,
    },
    {
      header: this.translate.instant('Titolo'),
      key: 'titolo',
      sortable: true,
    },
    {
      context: (x) => x.tag ? x.tag.slice(0, 5).join(', ') : '',
      header: this.translate.instant('Tags'),
      hideBreakpoints: [Breakpoints.XSmall],
      key: 'tags',
    },
    {
      header: this.translate.instant('Data creazione'),
      hideBreakpoints: [Breakpoints.XSmall, Breakpoints.Small],
      key: 'createdAt',
      sortable: true,
      template: 'date-time',
    },
    {
      align: 'right',
      header: '',
      key: 'context',
      showBreakpoints: [Breakpoints.XSmall, Breakpoints.Small, Breakpoints.Medium],
      template: 'context',
    },
  ];

  // tslint:disable-next-line:variable-name
  readonly pager$ = ((page?: number, limit?: number, sort?: TableSortType, filter?: TableFilterType) =>
    this.baseApi.index({ sort, page, limit, filter })
  );

  readonly contextMenus: Array<TableContextMenu<Base>> = [];

  constructor(
    private readonly baseApi: BaseApiService,
    private readonly roleService: RoleService,
    protected readonly route: ActivatedRoute,
    protected readonly router: Router,
    protected readonly swalService: SwalService,
    protected readonly translate: TranslateService,
    readonly uiCollectionService: UICollectionService,
  ) {
    super(translate, router, route, BaseFilterMeta, swalService, 'manager');

    if (this.roleService.hasCapability('Base', ['update'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Modifica'),
      });
      this.fields.push(basicEditTemplateOptions);
    } else if (this.roleService.hasCapability('Base', ['read', 'read+all'])) {
      this.contextMenus.push({
        action: (item) => this.editShowHandler(item),
        label: this.translate.instant('Visualizza'),
      });
      this.fields.push(basicShowTemplateOptions);
    }

    if (this.roleService.hasCapability('Base', ['delete'])) {
      this.contextMenus.push({
        action: (item) => item.id ? this.deleteHandler(item.id) : () => undefined,
        label: this.translate.instant('Elimina'),
      });
      this.fields.push(basicDeleteTemplateOptions);
    }
  }

  deleteConcreteHandler(id: string) {
    return this.baseApi.remove(id);
  }

  deleteSelectedConcreteHandler(ids: string[]) {
    const deleteApis = [new Promise(() => ids.map((id) => firstValueFrom(this.baseApi.remove(id))))];

    return from(Promise.all(deleteApis));
  }

  async addToCollectionHandler() {
    const success = await this.uiCollectionService.showCollectionPickerHandler(this.selectedItemIds, 'Base');
    if (success) {
      this.selectedItems = [];
    }
  }
}
